import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Input,
  Link,
  Select,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { registerDevTool } from '../../registry';
import { client } from '@php-beam/packages/frontend/src/api/client';
import LeadSearch from '../ResourceComposer/LeadSearch';

interface ActiveLead {
  id: number;
  business_name: string;
  url: string;
}

interface TerminationReason {
  value: string;
  label: string;
}

interface PayrollCustomerOption {
  id: number;
  status: string;
  client_id: string;
}

interface PendingRecordOption {
  id: number;
  label: string;
  check_date: string;
  start_date: string;
  end_date: string;
  remaining_amount: number;
}

function PayrollTools() {
  const [activeLead, setActiveLead] = useState<ActiveLead | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [terminationReasons, setTerminationReasons] = useState<TerminationReason[]>([]);
  const [selectedReason, setSelectedReason] = useState('');
  const [terminating, setTerminating] = useState(false);

  const [customers, setCustomers] = useState<PayrollCustomerOption[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [pendingRecords, setPendingRecords] = useState<PendingRecordOption[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [amount, setAmount] = useState('');
  const [simulating, setSimulating] = useState(false);

  const [populateStartDate, setPopulateStartDate] = useState('');
  const [populateEndDate, setPopulateEndDate] = useState('');
  const [populateCheckDate, setPopulateCheckDate] = useState('');
  const [populateStatus, setPopulateStatus] = useState('pending');
  const [populateIsInactivity, setPopulateIsInactivity] = useState(false);

  const toast = useToast();

  useEffect(() => {
    client
      .get<{ reasons: TerminationReason[] }>('/devtools/payroll/termination-reasons')
      .then((res) => setTerminationReasons(res.data.reasons));
  }, []);

  useEffect(() => {
    setCustomers([]);
    setSelectedCustomerId('');
    setPendingRecords([]);
    setSelectedRecordId('');
    setPaymentStatus('');
    setAmount('');

    if (!activeLead) return;

    client
      .get<{ customers: PayrollCustomerOption[] }>(
        `/devtools/payroll/lead/${activeLead.id}/customers`,
      )
      .then((res) => setCustomers(res.data.customers));
  }, [activeLead]);

  useEffect(() => {
    setPendingRecords([]);
    setSelectedRecordId('');
    setAmount('');

    if (!selectedCustomerId) return;

    client
      .get<{ records: PendingRecordOption[] }>(
        `/devtools/payroll/customer/${selectedCustomerId}/pending-records`,
      )
      .then((res) => setPendingRecords(res.data.records));
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedRecordId) {
      setAmount('');
      return;
    }

    const record = pendingRecords.find((r) => r.id === Number(selectedRecordId));
    if (record) {
      setAmount(String(record.remaining_amount));
    }
  }, [selectedRecordId]);

  const populate = async () => {
    if (!activeLead) return;

    setSubmitting(true);
    try {
      const res = await client.post<{
        message: string;
        task_id: number;
        record: {
          id: number;
          label: string;
          check_date: string;
          start_date: string;
          end_date: string;
          status: string;
        };
      }>(`/devtools/payroll/lead/${activeLead.id}/populate`, {
        ...(populateStartDate && { start_date: populateStartDate }),
        ...(populateEndDate && { end_date: populateEndDate }),
        ...(populateCheckDate && { check_date: populateCheckDate }),
        status: populateStatus,
        is_inactivity: populateIsInactivity,
      });

      const { record } = res.data;

      toast({
        title: 'Payroll record created',
        description: `Record #${record.id} — ${record.label} (${record.start_date} to ${record.end_date}, check: ${record.check_date})`,
        status: 'success',
        duration: 6000,
      });
    } catch (err: any) {
      toast({
        title: 'Failed to populate payroll record',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const simulateTermination = async () => {
    if (!activeLead || !selectedReason) return;

    setTerminating(true);
    try {
      const res = await client.post<{ message: string }>(
        `/devtools/payroll/lead/${activeLead.id}/terminate`,
        { reason: selectedReason },
      );

      toast({
        title: 'Termination simulated',
        description: res.data.message,
        status: 'success',
        duration: 6000,
      });
    } catch (err: any) {
      toast({
        title: 'Failed to simulate termination',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setTerminating(false);
    }
  };

  const simulatePayment = async () => {
    if (!selectedRecordId || !paymentStatus || !amount) return;

    setSimulating(true);
    try {
      const res = await client.post<{
        message: string;
        payment_id: number;
        status: string;
        amount: number;
        charge_id: number;
      }>(`/devtools/payroll/record/${selectedRecordId}/simulate-payment`, {
        status: paymentStatus,
        amount: parseFloat(amount),
      });

      toast({
        title: 'Payment simulated',
        description: `Payment #${res.data.payment_id} — ${res.data.status} ($${res.data.amount})`,
        status: res.data.status === 'approved' ? 'success' : 'warning',
        duration: 6000,
      });
    } catch (err: any) {
      toast({
        title: 'Failed to simulate payment',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setSimulating(false);
    }
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
          Select lead
        </Text>
        <LeadSearch
          onSelect={(lead) =>
            setActiveLead({ id: lead.id, business_name: lead.business_name, url: lead.url })
          }
        />
      </Box>

      {activeLead && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <Link href={activeLead.url} fontWeight="bold" color="purple.600">
              {activeLead.business_name}
            </Link>{' '}
            (ID: {activeLead.id})
          </Text>
          <Button
            size="xs"
            ml="auto"
            variant="ghost"
            onClick={() => setActiveLead(null)}
          >
            Clear
          </Button>
        </Alert>
      )}

      {activeLead && (
        <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Populate Records
          </Text>
          <VStack align="stretch" spacing={2}>
            <Box>
              <Text fontSize="xs" color="gray.500">Start Date (optional)</Text>
              <Input
                size="sm"
                type="date"
                value={populateStartDate}
                onChange={(e) => setPopulateStartDate(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">End Date (optional)</Text>
              <Input
                size="sm"
                type="date"
                value={populateEndDate}
                onChange={(e) => setPopulateEndDate(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Check Date (optional)</Text>
              <Input
                size="sm"
                type="date"
                value={populateCheckDate}
                onChange={(e) => setPopulateCheckDate(e.target.value)}
              />
            </Box>
            <Select
              size="sm"
              value={populateStatus}
              onChange={(e) => setPopulateStatus(e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="complete">Complete</option>
            </Select>
            <Checkbox
              size="sm"
              isChecked={populateIsInactivity}
              onChange={(e) => setPopulateIsInactivity(e.target.checked)}
            >
              Is Inactivity
            </Checkbox>
            <Button
              size="sm"
              colorScheme="purple"
              onClick={populate}
              isLoading={submitting}
            >
              Populate Payroll Record
            </Button>
          </VStack>
        </Box>
      )}

      {activeLead && (
        <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Simulate Payment
          </Text>
          <VStack align="stretch" spacing={2}>
            <Select
              size="sm"
              placeholder="Select payroll customer"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  Customer #{c.id} — {c.status} {c.client_id ? `(${c.client_id})` : ''}
                </option>
              ))}
            </Select>

            {selectedCustomerId && (
              <Select
                size="sm"
                placeholder="Select pending record"
                value={selectedRecordId}
                onChange={(e) => setSelectedRecordId(e.target.value)}
              >
                {pendingRecords.map((r) => (
                  <option key={r.id} value={r.id}>
                    #{r.id} — {r.label} ({r.check_date})
                  </option>
                ))}
              </Select>
            )}

            {selectedRecordId && (
              <>
                <Select
                  size="sm"
                  placeholder="Select payment status"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                >
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                </Select>

                <Input
                  size="sm"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />

                <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={simulatePayment}
                  isLoading={simulating}
                  isDisabled={!paymentStatus || !amount}
                >
                  Simulate Payment
                </Button>
              </>
            )}
          </VStack>
        </Box>
      )}

      {activeLead && (
        <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Simulate Termination
          </Text>
          <VStack align="stretch" spacing={2}>
            <Select
              size="sm"
              placeholder="Select reason"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {terminationReasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.value.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              colorScheme="red"
              onClick={simulateTermination}
              isLoading={terminating}
              isDisabled={!selectedReason}
            >
              Simulate Termination
            </Button>
          </VStack>
        </Box>
      )}
    </VStack>
  );
}

registerDevTool({
  name: 'Payroll Tools',
  component: PayrollTools,
});
